import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { GeoJSON as LeafletGeoJSON, MapContainer, TileLayer, useMap } from 'react-leaflet';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  BarElement
} from 'chart.js';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import {
  aggregateWalkabilityRows,
  CLASS_ORDER,
  classifyFromPercentile,
  fetchCountyWalkabilityRows,
  fetchPlaceTractSet,
  normalizeTractGeofips,
  pickPercentile,
  resolveCountyPrefixForLocation,
  tractGeoidsFromWalkabilityRows,
  type WalkClass,
} from '../../services/walkabilityTransportMetrics';
import { isUsableLatLngBounds, safeFitBounds } from '../../utils/leafletBounds';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/** MapContainer only uses initial center/zoom; fit after data loads so the view matches tracts in scope. */
function FitTractLayerBounds({ data }: { data: { type: 'FeatureCollection'; features: unknown[] } | null }) {
  const map = useMap();
  useEffect(() => {
    if (!data || (data as { type?: string }).type !== 'FeatureCollection') return;
    const features = (data as { features?: unknown[] }).features;
    if (!features?.length) return;
    try {
      const layer = L.geoJSON(data as never);
      const b = layer.getBounds();
      if (isUsableLatLngBounds(b)) {
        safeFitBounds(map, b, { padding: [28, 28], maxZoom: 14 });
      }
    } catch {
      // ignore invalid geometries
    }
  }, [data, map]);
  return null;
}

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

interface WalkabilityTractSectionProps {
  location: string;
  geofips: number | null;
}

const CLASS_COLORS: Record<WalkClass, string> = {
  'Highly walkable (80–100)': '#15803d',
  'Above average (60–80)': '#22c55e',
  'Moderate (40–60)': '#eab308',
  'Below average (20–40)': '#f97316',
  'Least walkable (0–20)': '#b91c1c',
  Unknown: '#94a3b8'
};

const WalkabilityTractSection: React.FC<WalkabilityTractSectionProps> = ({ location, geofips }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countyRows, setCountyRows] = useState<Record<string, unknown>[]>([]);
  const [placeTractIds, setPlaceTractIds] = useState<Set<string> | null>(null);
  const [scopeLabel, setScopeLabel] = useState<string>('');
  const [geoJsonData, setGeoJsonData] = useState<any>(null);

  const aggregated = useMemo(
    () => aggregateWalkabilityRows(countyRows, placeTractIds),
    [countyRows, placeTractIds]
  );

  const walkByGeoid = useMemo(() => {
    const m = new Map<string, { percentile: number | null; klass: WalkClass }>();
    const placeOnly = placeTractIds !== null;
    for (const row of countyRows) {
      const raw = row.geofips ?? (row as any).tract ?? (row as any).geoid;
      const g = normalizeTractGeofips(raw as string | number);
      const geoid = g.length === 11 ? g : g.padStart(11, '0');
      if (placeOnly && !placeTractIds!.has(geoid)) continue;
      const pct = pickPercentile(row);
      m.set(geoid, { percentile: pct, klass: classifyFromPercentile(pct) });
    }
    return m;
  }, [countyRows, placeTractIds]);

  useEffect(() => {
    const loadGeo = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL || ''}/data/california_tracts_optimized.geojson`);
        const data = await response.json();
        setGeoJsonData(data);
      } catch (e) {
        console.warn('WalkabilityTractSection: geojson load failed', e);
      }
    };
    loadGeo();
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      setCountyRows([]);
      setPlaceTractIds(null);
      setScopeLabel('');

      try {
        const { countyPrefix, scope, placeGeofips } = await resolveCountyPrefixForLocation(location);

        if (scope === 'state') {
          setError('Select a city or county to view tract walkability.');
          setLoading(false);
          return;
        }

        if (!countyPrefix) {
          setError('Could not resolve county for this location.');
          setLoading(false);
          return;
        }

        const rows = await fetchCountyWalkabilityRows(countyPrefix);
        setCountyRows(rows);

        const tractGeoids = tractGeoidsFromWalkabilityRows(rows);
        const locType = supabaseLocationService.getLocationType(location.trim());
        if (locType === 'city') {
          const tractFilter = placeGeofips
            ? await fetchPlaceTractSet(placeGeofips, location.trim(), tractGeoids)
            : new Set<string>();
          setPlaceTractIds(tractFilter);
          setScopeLabel(
            tractFilter.size > 0
              ? `Tracts intersecting ${location.trim()} (relationship_trct_place_20)`
              : `No tract–place crosswalk rows for this city (place GEOID ${placeGeofips ?? '—'}). Metrics and map show no tracts until geofips matches stplc or stplcname in relationship_trct_place_20.`
          );
        } else {
          setPlaceTractIds(null);
          setScopeLabel(`All census tracts in county (prefix ${countyPrefix})`);
        }
      } catch (e) {
        console.error('WalkabilityTractSection:', e);
        setError(e instanceof Error ? e.message : 'Failed to load walkability data.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [location, geofips]);

  const barCounts = aggregated.counts;

  const barData = useMemo(
    () => ({
      labels: CLASS_ORDER.filter((c) => c !== 'Unknown' || barCounts.Unknown > 0),
      datasets: [
        {
          label: 'Tract count',
          data: CLASS_ORDER.filter((c) => c !== 'Unknown' || barCounts.Unknown > 0).map((c) => barCounts[c]),
          backgroundColor: CLASS_ORDER.filter((c) => c !== 'Unknown' || barCounts.Unknown > 0).map((c) => CLASS_COLORS[c])
        }
      ]
    }),
    [barCounts]
  );

  const onEachFeature = useCallback(
    (feature: any, layer: L.Layer) => {
      const geoid = String(feature?.properties?.GEOID || '').padStart(11, '0');
      const w = walkByGeoid.get(geoid);
      if (!w) return;
      layer.bindPopup(
        `<div style="font-size:12px"><strong>GEOID ${geoid}</strong><br/>` +
          (w.percentile != null ? `Percentile: ${w.percentile.toFixed(1)}<br/>` : '') +
          `${w.klass}</div>`
      );
    },
    [walkByGeoid]
  );

  const geoJsonStyle = useCallback(
    (feature: any) => {
      const geoid = String(feature?.properties?.GEOID || '').padStart(11, '0');
      const w = walkByGeoid.get(geoid);
      const k = w?.klass ?? 'Unknown';
      return {
        fillColor: CLASS_COLORS[k],
        weight: 0.5,
        color: '#1e293b',
        fillOpacity: 0.82
      };
    },
    [walkByGeoid]
  );

  const filteredGeoJson = useMemo(() => {
    if (!geoJsonData?.features?.length || walkByGeoid.size === 0) return null;
    const keys = new Set(walkByGeoid.keys());
    const features = geoJsonData.features.filter((f: any) => {
      const geoid = String(f?.properties?.GEOID || '').padStart(11, '0');
      return keys.has(geoid);
    });
    if (features.length === 0) return null;
    return { type: 'FeatureCollection' as const, features };
  }, [geoJsonData, walkByGeoid]);

  if (loading) {
    return (
      <div style={{ padding: '1rem', color: '#64748b' }}>
        Loading EPA National Walkability Index (tract)…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', color: '#b91c1c' }}>
        {error}
      </div>
    );
  }

  const avg = aggregated.avgPercentile;
  const meanLabel =
    avg != null && Number.isFinite(avg) ? `${avg.toFixed(1)}` : '—';
  const highlyPct =
    aggregated.pctHighlyWalkableTracts != null && Number.isFinite(aggregated.pctHighlyWalkableTracts)
      ? `${aggregated.pctHighlyWalkableTracts.toFixed(1)}%`
      : '—';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <KeyMetrics>
        <KeyMetric value={meanLabel} label="Mean tract percentile (EPA)" color="#1e3a8a" />
        <KeyMetric
          value={aggregated.tractCount > 0 ? String(aggregated.tractCount) : '—'}
          label="Tracts in scope"
          color="#059669"
        />
        <KeyMetric value={highlyPct} label="Highly walkable tracts (80–100)" color="#15803d" />
      </KeyMetrics>

      {aggregated.tractCount > 0 && avg != null && (
        <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.55, color: '#334155' }}>
          For <strong>{location.trim()}</strong>, the mean EPA National Walkability Index percentile across{' '}
          <strong>{aggregated.tractCount}</strong> census tract{aggregated.tractCount === 1 ? '' : 's'} in scope is{' '}
          <strong>{avg.toFixed(1)}</strong> (California tract distribution). Tracts in the highly walkable bucket (80th–100th
          percentile) account for <strong>{highlyPct}</strong> of tracts with classified walkability — matching the Livability
          index Mobility row for this location.
        </p>
      )}

      {scopeLabel ? (
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569' }}>{scopeLabel}</p>
      ) : null}

      <div style={{ height: 380, width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        {filteredGeoJson ? (
          <MapContainer
            key={location}
            center={[36.7783, -119.4179]}
            zoom={8}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitTractLayerBounds data={filteredGeoJson} />
            <LeafletGeoJSON data={filteredGeoJson} style={geoJsonStyle as any} onEachFeature={onEachFeature} />
          </MapContainer>
        ) : (
          <div style={{ padding: '1rem', color: '#64748b' }}>
            {countyRows.length === 0
              ? 'No rows in walkability_tract for this county (check Supabase table and RLS).'
              : 'Could not match tracts to GeoJSON (GEOID).'}
          </div>
        )}
      </div>

      <div style={{ maxWidth: 720 }}>
        <Bar
          data={barData}
          options={{
            responsive: true,
            plugins: {
              legend: { display: false },
              title: { display: true, text: 'Tracts by walkability class (CA percentile buckets)' }
            },
            scales: {
              x: { ticks: { maxRotation: 45, minRotation: 35, font: { size: 10 } } },
              y: { beginAtZero: true, ticks: { precision: 0 } }
            }
          }}
        />
      </div>
    </div>
  );
};

export default WalkabilityTractSection;
